'use client'

import { useMemo, useState } from 'react'
import { useGetTopupPackagesQuery } from '@/api/user/organization/credits/get-topup-packages-query'
import { useCreateTopupCheckoutMutation } from '@/api/user/organization/credits/create-topup-checkout-mutation'
import { useGetCreditHistoryQuery } from '@/api/user/organization/credits/get-credit-history-query'
import { useGetOrganizationBillingQuery } from '@/api/user/organization/get-organization-billing-query'
import { useOrganizationId } from '@/features/organization/use-organization-id'
import { useBillingCapabilities } from '@/features/subscription/billing/billing-capabilities'
import { setBillingBlockerRedirectUrl } from '@/features/subscription/billing/billing-blocker'
import { BillingPlanCreditsIncluded } from '@/features/subscription/billing/billing-plans/billing-plan-credits-included'
import { DimensionsFadeContainer } from '@/features/animations/dimensions-fade-container'
import { CreditPoolHistoryList } from '../../credit-pool-card'
import { SettingsSection } from '../../settings-section'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { toast } from '@/features/toast/toast'
import { cn } from '@/utils/styles/cn'
import { Coins, Loader2, Minus, Plus } from 'lucide-react'

const PACKAGE_LABELS: Record<string, string> = {
    credits_2500: 'Starter',
    credits_5000: 'Standard',
    credits_10000: 'Pro',
}

const MAX_QUANTITY = 100
const ITEMS_PER_PAGE = 10

export const TopupsTab = () => {
    const organizationId = useOrganizationId()
    const { canPurchaseTopupCredits } = useBillingCapabilities()
    const [selectedPackage, setSelectedPackage] = useState<string | null>(null)
    const [quantity, setQuantity] = useState(1)
    const [page, setPage] = useState(1)

    const { data: billingAndUsage } = useGetOrganizationBillingQuery(organizationId)
    const activeSubscription = billingAndUsage?.billing.activeSubscription
    const hasActiveSubscription = activeSubscription?.hasSubscription === true && !activeSubscription?.isInTrial

    const { data: packagesData, isLoading: isLoadingPackages } = useGetTopupPackagesQuery(organizationId)
    const { mutateAsync: createCheckout, isPending: isCreatingCheckout } =
        useCreateTopupCheckoutMutation(organizationId)

    const { data: historyData, isLoading: isLoadingHistory } = useGetCreditHistoryQuery(organizationId, {
        page,
        limit: ITEMS_PER_PAGE,
        type: 'TOP_UP',
    })

    const packages = useMemo(() => packagesData?.packages ?? [], [packagesData])
    const creditPools = historyData?.data?.creditPools ?? []
    const meta = historyData?.meta
    const totalPages = meta ? Math.ceil(meta.totalCount / meta.limit) : 1

    const selectedPkg = useMemo(() => packages.find((p) => p.package === selectedPackage), [packages, selectedPackage])

    if (!canPurchaseTopupCredits) {
        return (
            <div className="flex flex-col gap-8">
                <SettingsSection
                    title="Credits managed by Shopify"
                    description="Your credits are included in your Shopify subscription. Manage your plan in Shopify Admin to change your credit allocation."
                />
            </div>
        )
    }
    const totalCredits = selectedPkg ? selectedPkg.credits * quantity : 0
    const totalAmount = selectedPkg ? selectedPkg.amount * quantity : 0

    const handleSelectPackage = (pkg: string) => {
        if (pkg !== selectedPackage) {
            setQuantity(1)
        }
        setSelectedPackage(pkg)
    }

    const handlePurchase = async () => {
        if (!selectedPackage) {
            toast.error('Please select a package')
            return
        }

        try {
            const result = await createCheckout({
                package: selectedPackage as 'credits_2500' | 'credits_5000' | 'credits_10000',
                quantity,
            })

            if (result.url) {
                // Bring the user back to where they started the top-up (e.g. a
                // blocked agent chat) instead of the generic /routing landing.
                setBillingBlockerRedirectUrl(window.location.pathname)
                window.location.href = result.url
            } else {
                toast.error('Failed to create checkout session')
            }
        } catch {
            toast.error('Failed to create checkout session')
        }
    }

    const formatPrice = (amount: number) => `$${(amount / 100).toLocaleString()}`
    const formatCredits = (credits: number) => credits.toLocaleString()

    const isDisabled = !hasActiveSubscription

    return (
        <div className="flex flex-col gap-8">
            <SettingsSection
                title="Buy Credits"
                description="Purchase additional credits for your workspace. Credits never expire."
            >
                <Alert variant="info">
                    <AlertDescription>
                        Topping up credits regularly? Would you like volume discounts?{' '}
                        <a href="mailto:lukas@superscale.ai" className="font-medium underline">
                            Contact us
                        </a>{' '}
                        for a custom plan with discounted credit pricing.
                    </AlertDescription>
                </Alert>
                {isLoadingPackages ? (
                    <div className="flex items-center justify-center py-8">
                        <Loader2 className="w-6 h-6 animate-spin text-[#6B7280]" />
                    </div>
                ) : packages.length === 0 ? (
                    <div className="text-center py-8 text-[13px] text-[#6B7280]">
                        No packages available at this time.
                    </div>
                ) : (
                    <div className="flex flex-col gap-4">
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                            {packages.map((pkg) => {
                                const isSelected = !isDisabled && selectedPackage === pkg.package

                                const card = (
                                    <button
                                        key={pkg.package}
                                        type="button"
                                        onClick={() => !isDisabled && handleSelectPackage(pkg.package)}
                                        className={cn(
                                            'flex flex-col rounded-[15px] bg-white text-left transition-colors py-3 px-4 pb-4 border-2',
                                            isDisabled
                                                ? 'border-[#4747471A] cursor-default'
                                                : isSelected
                                                  ? 'border-[#FF631B]'
                                                  : 'border-[#4747471A] hover:border-[#D1D5DB]'
                                        )}
                                    >
                                        <div className="text-[12px] text-[#6B7280] mb-1 font-medium uppercase tracking-wide">
                                            {PACKAGE_LABELS[pkg.package] ?? 'Top-up'}
                                        </div>

                                        <div className="text-[14px] text-[#171717] flex items-center mb-2">
                                            <div className="text-[24px] leading-[100%] font-semibold">
                                                {formatPrice(pkg.amount)}
                                            </div>
                                            <div className="text-[14px] leading-[100%] font-normal ml-2">one-time</div>
                                        </div>

                                        <BillingPlanCreditsIncluded
                                            credits={formatCredits(pkg.credits)}
                                            className="mb-3"
                                        />

                                        <div
                                            className={cn(
                                                'w-full rounded-lg py-2 text-center text-[14px] font-medium transition-colors',
                                                isDisabled
                                                    ? 'bg-[#F5F5F5] text-[#9CA3AF]'
                                                    : isSelected
                                                      ? 'bg-[#FF631B] text-white'
                                                      : 'bg-[#F5F5F5] text-[#374151] hover:bg-[#EBEBEB]'
                                            )}
                                        >
                                            {isSelected ? 'Selected' : 'Select package'}
                                        </div>
                                    </button>
                                )

                                if (isDisabled) {
                                    return (
                                        <Tooltip key={pkg.package}>
                                            <TooltipTrigger asChild>{card}</TooltipTrigger>
                                            <TooltipContent>
                                                Credit top-ups require an active subscription
                                            </TooltipContent>
                                        </Tooltip>
                                    )
                                }

                                return card
                            })}
                        </div>

                        {selectedPkg && (
                            <div className="rounded-xl border border-[#E5E7EB] bg-[#FAFAFA] p-4">
                                <div className="flex items-center justify-between">
                                    <span className="text-[13px] font-medium text-[#6B7280]">Quantity</span>
                                    <div className="flex items-center gap-1">
                                        <button
                                            type="button"
                                            onClick={() => setQuantity((q) => Math.max(1, q - 1))}
                                            disabled={quantity <= 1}
                                            className={cn(
                                                'w-8 h-8 rounded-lg flex items-center justify-center transition-colors',
                                                quantity <= 1
                                                    ? 'text-[#D1D5DB] cursor-not-allowed'
                                                    : 'text-[#6B7280] hover:bg-[#E5E7EB] active:bg-[#D1D5DB]'
                                            )}
                                        >
                                            <Minus className="w-4 h-4" />
                                        </button>
                                        <input
                                            type="text"
                                            inputMode="numeric"
                                            value={quantity}
                                            onChange={(e) => {
                                                const val = parseInt(e.target.value, 10)
                                                if (!isNaN(val) && val >= 1 && val <= MAX_QUANTITY) {
                                                    setQuantity(val)
                                                } else if (e.target.value === '') {
                                                    setQuantity(1)
                                                }
                                            }}
                                            className="w-12 h-8 text-center text-[15px] font-semibold text-[#171717] bg-white border border-[#E5E7EB] rounded-lg focus:outline-none focus:border-[#FF631B] focus:ring-1 focus:ring-[#FF631B]/20"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setQuantity((q) => Math.min(MAX_QUANTITY, q + 1))}
                                            disabled={quantity >= MAX_QUANTITY}
                                            className={cn(
                                                'w-8 h-8 rounded-lg flex items-center justify-center transition-colors',
                                                quantity >= MAX_QUANTITY
                                                    ? 'text-[#D1D5DB] cursor-not-allowed'
                                                    : 'text-[#6B7280] hover:bg-[#E5E7EB] active:bg-[#D1D5DB]'
                                            )}
                                        >
                                            <Plus className="w-4 h-4" />
                                        </button>
                                    </div>
                                </div>
                                {quantity > 1 && (
                                    <div className="mt-2 pt-2 border-t border-[#E5E7EB] flex items-center justify-between text-[13px]">
                                        <span className="text-[#9CA3AF]">
                                            {quantity} x {formatCredits(selectedPkg.credits)} credits
                                        </span>
                                        <span className="font-semibold text-[#FF631B]">
                                            {formatCredits(totalCredits)} credits for {formatPrice(totalAmount)}
                                        </span>
                                    </div>
                                )}
                            </div>
                        )}

                        <div>
                            <Button
                                onClick={handlePurchase}
                                disabled={!selectedPackage || isCreatingCheckout || !hasActiveSubscription}
                                isLoading={isCreatingCheckout}
                                variant="primary"
                                size="sm"
                                className="gap-2"
                                childContainerClassName="gap-0"
                            >
                                <Coins className="w-4 h-4 mr-2" />
                                <span>Purchase</span>
                                <DimensionsFadeContainer
                                    childrenKey={selectedPkg ? formatPrice(totalAmount) : 'none'}
                                    animateHeight={false}
                                    animateWidth={true}
                                    animateOpacity={true}
                                    animatePresenceMode="popLayout"
                                    childrenClassName="flex items-center"
                                >
                                    {selectedPkg ? <span className="ml-2">{formatPrice(totalAmount)}</span> : null}
                                </DimensionsFadeContainer>
                            </Button>
                        </div>
                    </div>
                )}
            </SettingsSection>

            <SettingsSection title="Top-up History">
                <CreditPoolHistoryList
                    pools={creditPools}
                    isLoading={isLoadingHistory}
                    emptyIcon={Coins}
                    emptyMessage="No top-up purchases yet."
                    page={page}
                    totalPages={totalPages}
                    onPageChange={setPage}
                />
            </SettingsSection>
        </div>
    )
}
